/**
 * Authentication Integration for HTTP Client
 * Provides clean integration between authentication system and HTTP client
 */

import { HttpClient, ApiError, GetAuthTokenFunction, ErrorInterceptor } from '@http';
import { tokenStorage, decodeUserFromAccessToken } from './storage';
import { AuthError, AuthErrorType } from './types';

/**
 * Create authentication token provider for HTTP client
 * Handles token retrieval and automatic refresh
 */
export const createAuthTokenProvider = (): GetAuthTokenFunction => {
    let isRefreshing = false;
    let refreshPromise: Promise<string | null> | null = null;

    return async (): Promise<string | null> => {
        try {
            const tokens = tokenStorage.getTokens();
            
            if (!tokens) {
                return null;
            }

            // If token is not expired, return it immediately
            if (!tokenStorage.isTokenPotentiallyExpired()) {
                return tokens.access_token;
            }

            // If already refreshing, wait for the existing refresh
            if (isRefreshing && refreshPromise) {
                return await refreshPromise;
            }

            // Start refresh process
            isRefreshing = true;
            console.log('Start refresh process');
            refreshPromise = refreshAccessToken(tokens.refresh_token);

            try {
                const newToken = await refreshPromise;
                return newToken;
            } finally {
                isRefreshing = false;
                refreshPromise = null;
            }
        } catch (error) {
            console.error('Error getting auth token:', error);
            return null;
        }
    };
};

/**
 * Refresh access token using refresh token
 */
async function refreshAccessToken(refreshToken: string): Promise<string | null> {
    try {
        // Create a basic HTTP client for auth requests (no auth to avoid circular dependency)
        const authClient = new HttpClient();

        const response = await authClient.post<{
            access_token: string;
            refresh_token: string;
            token_type: string;
        }>('/auth/refresh', { refresh_token: refreshToken });

        // Update stored tokens
        tokenStorage.setTokens({
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token,
            token_type: response.data.token_type
        });

        const storedUser = decodeUserFromAccessToken(response.data.access_token);
        tokenStorage.setUser(storedUser);

        return response.data.access_token;
    } catch (error) {
        console.warn('Token refresh failed:', error);
        
        // Clear all tokens on refresh failure
        tokenStorage.clearAll();
        
        // Redirect to login page
        if (typeof window !== 'undefined') {
            window.location.href = '/login';
        }
        
        return null;
    }
}

/**
 * Create error interceptor for handling authentication errors
 */
export const createAuthErrorInterceptor = (): ErrorInterceptor => {
    return async (error: ApiError): Promise<never> => {
        // Handle authentication errors
        if (error.status === 401) {
            // Clear stored tokens
            tokenStorage.clearAll();
            
            // Redirect to login page
            if (typeof window !== 'undefined') {
                window.location.href = '/login';
            }
            
            throw new AuthError(
                AuthErrorType.UNAUTHORIZED,
                'Authentication required. Please login again.'
            );
        }
        
        // Handle authorization errors
        // if (error.status === 403) {
        //     throw new AuthError(
        //         AuthErrorType.AUTHORIZATION_ERROR,
        //         'Access denied. You do not have permission to access this resource.'
        //     );
        // }
        
        // Re-throw other errors as-is
        throw error;
    };
};

/**
 * Create authenticated HTTP client with proper configuration
 */
export function createAuthenticatedHttpClient(): HttpClient {
    const client = new HttpClient({
        getAuthToken: createAuthTokenProvider()
    });

    // Add authentication error handling
    client.addErrorInterceptor(createAuthErrorInterceptor());

    return client;
}

/**
 * Singleton authenticated HTTP client instance
 */
let authenticatedClient: HttpClient | null = null;

/**
 * Get the singleton authenticated HTTP client
 */
export function getAuthenticatedHttpClient(): HttpClient {
    if (!authenticatedClient) {
        authenticatedClient = createAuthenticatedHttpClient();
    }
    return authenticatedClient;
}

/**
 * Reset the authenticated HTTP client (useful for testing or user switching)
 */
export function resetAuthenticatedHttpClient(): void {
    authenticatedClient = null;
}